import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonGroup, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import {
  AccountTree as ChainIcon,
  ArrowDropDown as ArrowIcon,
  AutoAwesome as LearningIcon,
  Edit as EditIcon,
  ListAlt as RunsIcon,
  ShieldOutlined as ShieldIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';
import { ReliabilityDialog } from '../reliability/ReliabilityDialog';
import { learningPath } from '../../services/learningApi';

interface Props {
  kind: 'function' | 'chain';
  id: string;
  name?: string;
  /** False when an admin is viewing another customer: settings belong to the signed-in account. */
  editable: boolean;
  /** Adds Runs and Performance to the menu (for pages outside the function's own page). */
  showViews?: boolean;
  /** Carried onto Runs/Performance links so admins stay on the same customer. */
  viewParams?: URLSearchParams;
  onReliabilitySaved?: () => void;
}

const NOT_OWNER = "Settings belong to the signed-in account, so editing is off while you're viewing another customer.";

/** Split button: edit the function or chain, with its reliability, self-improvement and run views in the menu. */
export const QuickLinks: React.FC<Props> = ({ kind, id, name, editable, showViews, viewParams, onReliabilitySaved }) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [reliabilityOpen, setReliabilityOpen] = useState(false);
  const isChain = kind === 'chain';

  const go = (path: string) => {
    setAnchor(null);
    navigate(path);
  };
  const view = (tab?: 'performance') => {
    const params = new URLSearchParams(viewParams);
    if (tab) params.set('tab', tab);
    const qs = params.toString();
    go(`/dashboard/function-runs/${id}${qs ? `?${qs}` : ''}`);
  };

  const editButton = (
    <Button
      startIcon={isChain ? <ChainIcon /> : <EditIcon />}
      disabled={!editable}
      onClick={() => navigate(isChain ? `/dashboard?chain=${id}` : `/dashboard/ai-functions?id=${id}`)}
    >
      {isChain ? 'Open in chain editor' : 'Edit function'}
    </Button>
  );

  return (
    <>
      <ButtonGroup size="small" variant="outlined" sx={{ flexShrink: 0, bgcolor: 'background.paper' }}>
        {editable ? (
          editButton
        ) : (
          <Tooltip title={NOT_OWNER}>
            <span>{editButton}</span>
          </Tooltip>
        )}
        <Button aria-label="More options" onClick={e => setAnchor(e.currentTarget)} sx={{ px: 0.5 }}>
          <ArrowIcon />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        {showViews && !isChain && [
          <MenuItem key="runs" onClick={() => view()}>
            <ListItemIcon>
              <RunsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Runs" secondary="Every run of this function" />
          </MenuItem>,
          <MenuItem key="perf" onClick={() => view('performance')}>
            <ListItemIcon>
              <ChartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Performance" secondary="Speed, outcomes, time limit, cost" />
          </MenuItem>,
          <Divider key="div" />,
        ]}
        <MenuItem
          disabled={!editable}
          onClick={() => {
            setAnchor(null);
            setReliabilityOpen(true);
          }}
        >
          <ListItemIcon>
            <ShieldIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Reliability settings" secondary="Time limit, backup models, retries" />
        </MenuItem>
        <MenuItem disabled={!editable} onClick={() => go(learningPath(kind, id))}>
          <ListItemIcon>
            <LearningIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Self-improvement" secondary="What it has learned, and its settings" />
        </MenuItem>
        {!editable && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, pt: 1, maxWidth: 280 }}>
            {NOT_OWNER}
          </Typography>
        )}
      </Menu>
      {reliabilityOpen && (
        <ReliabilityDialog
          open
          onClose={() => setReliabilityOpen(false)}
          targetType={kind}
          targetId={id}
          targetName={name}
          onSaved={() => onReliabilitySaved?.()}
        />
      )}
    </>
  );
};
